import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AddCommasToAmount } from '../../../helper/Utils';
import DataTable from 'react-data-table-component';
import { ADD_CHALLAN_FAIL, ADD_CHALLAN_START } from '../../../RTK/Slices/ChallanSlice';
import { addOtherChallan, getHeadOfAccounts } from '../../../helper/Api';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import {
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
} from '@mui/material';
import { AiOutlineVerticalAlignBottom } from 'react-icons/ai';
import Spinner from '../../../components/Tools/spinner';
import CustomBreadcrumbs from '../../../components/Tools/Breadcumbs';
import './AddOtherChallan.css';

const MultiLineHeader = ({ lines, align = 'center' }) => (
    <div className={`other-col-header other-col-header--${align}`}>
        {lines.map((line, idx) => (
            <React.Fragment key={idx}>
                {idx > 0 && <br />}
                {line}
            </React.Fragment>
        ))}
    </div>
);

const CenterCell = ({ children }) => (
    <div className="other-cell-center">{children}</div>
);

const actionIconBtnStyle = {
    width: 28,
    height: 28,
    padding: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '6px',
    background: 'transparent',
};

const formatAmount = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const num = Number(value);
    if (Number.isNaN(num)) return value;
    return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const getApprovalTypeLabel = (paymentType) => {
    if (paymentType === '1') return 'FULL PAYMENT';
    if (paymentType === '2') return 'PART PAYMENT';
    return '';
};

const AddOtherChallan = () => {
    const [loading, setLoading] = useState(false);
    const { state } = useLocation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [data] = useState(state?.data ? state.data : []);
    const [headAccounts, setHeadAccounts] = useState([]);

    const [formData, setFormData] = useState({
        id: '',
        invoices: [],
        ref_doc_no: '',
        challan_date: '',
        challan_url: '',
        attachments: [],
        head_of_account_id: '',
    });

    const customStyles = {
        table: {
            style: {
                width: '100%',
            },
        },
        headRow: {
            style: {
                minHeight: '52px',
            },
        },
        headCells: {
            style: {
                fontWeight: 400,
                fontSize: '14px',
                whiteSpace: 'normal',
                overflow: 'visible',
                textOverflow: 'clip',
                wordBreak: 'break-word',
                lineHeight: 1.35,
                paddingTop: '8px',
                paddingBottom: '8px',
            },
        },
    };

    const handleDownload = (url) => {
        if (!url) return;
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.rel = 'noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const columns = [
        {
            name: <MultiLineHeader lines={['Invoice No/', 'Date']} align="left" />,
            selector: (row) => {
                if (row.isFooter) return row.invoice_no;
                const invoiceDate = row.invoice_date
                    ? moment(row.invoice_date, ['YYYY-MM-DD', 'DD-MM-YYYY']).format('DD-MM-YYYY')
                    : '';
                return (
                    <div>
                        <div><b>{row.invoice_no || ''}</b></div>
                        {invoiceDate && <div className="text-muted fs-12px">{invoiceDate}</div>}
                    </div>
                );
            },
            wrap: true,
            minWidth: '130px',
            grow: 1.2,
        },
        {
            name: <MultiLineHeader lines={['Beneficiary', 'Details']} align="left" />,
            selector: (row) => {
                if (row.isFooter) return '';
                return (
                    <div>
                        <div>{row.company_name || ''}</div>
                        {row.pan_no && (
                            <div className="text-muted fs-12px">
                                <b>Pan No.:</b> {row.pan_no}
                            </div>
                        )}
                    </div>
                );
            },
            wrap: true,
            minWidth: '170px',
            grow: 2,
        },
        {
            name: <MultiLineHeader lines={['Voucher No', 'Voucher Date']} align="left" />,
            selector: (row) => {
                if (row.isFooter) return '';
                const voucherDate = row.voucher_date
                    ? moment(row.voucher_date, ['YYYY-MM-DD', 'DD-MM-YYYY']).format('DD-MM-YYYY')
                    : '';
                return (
                    <div>
                        <div><b>{row.voucher_no || ''}</b></div>
                        {voucherDate && <div className="text-muted fs-12px">{voucherDate}</div>}
                    </div>
                );
            },
            wrap: true,
            minWidth: '150px',
            grow: 1.5,
        },
        {
            name: <MultiLineHeader lines={['Transaction', 'Date']} align="center" />,
            selector: (row) => {
                if (row.isFooter) return '';
                return row.transaction_date
                    ? moment(row.transaction_date, ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD']).format('DD-MM-YYYY')
                    : '';
            },
            minWidth: '105px',
            grow: 1,
            center: true,
        },
        {
            name: <MultiLineHeader lines={['Taxable', 'Amount']} align="center" />,
            selector: (row) => (row.isFooter ? '' : formatAmount(row.taxable_amount)),
            minWidth: '105px',
            grow: 1,
            center: true,
        },
        {
            name: <MultiLineHeader lines={['Invoice', 'Amount']} align="center" />,
            selector: (row) => (row.isFooter ? '' : formatAmount(row.payable_amount)),
            minWidth: '105px',
            grow: 1,
            center: true,
        },
        {
            name: <MultiLineHeader lines={['Sanctioned', 'Amount']} align="center" />,
            selector: (row) => (row.isFooter ? '' : formatAmount(row.sanction_amount)),
            minWidth: '115px',
            grow: 1,
            center: true,
        },
        {
            name: 'Other Deduction',
            selector: (row) => (
                <CenterCell>
                    {row.isFooter ? row.other_deduction : formatAmount(row.other_deduction)}
                </CenterCell>
            ),
            minWidth: '120px',
            grow: 1,
            center: true,
        },
        {
            name: 'Actions',
            selector: (row) => (
                row.isFooter ? null : (
                    <div className="d-flex align-items-center justify-content-center" style={{ gap: '6px', width: '100%' }}>
                        <button
                            type="button"
                            title="Download Attachment"
                            disabled={!row?.invoice}
                            onClick={() => handleDownload(row.invoice)}
                            style={{
                                ...actionIconBtnStyle,
                                border: '1px solid #1976d2',
                                color: '#1976d2',
                                cursor: row?.invoice ? 'pointer' : 'not-allowed',
                                opacity: row?.invoice ? 1 : 0.5,
                            }}
                        >
                            <AiOutlineVerticalAlignBottom size={16} />
                        </button>
                    </div>
                )
            ),
            width: '100px',
            minWidth: '100px',
            grow: 0,
            center: true,
        },
    ];

    const calculateTotalAmount = (propertyName) => {
        let total = 0;
        data.forEach((row) => {
            const depositAmount = Number(parseFloat(row[propertyName]));
            if (!Number.isNaN(depositAmount)) {
                total += depositAmount;
            }
        });
        return total.toFixed(2);
    };

    const totalOtherDeduction = calculateTotalAmount('other_deduction');

    const footerRow = {
        isFooter: true,
        invoice_no: <b>Total</b>,
        other_deduction: <b>{AddCommasToAmount(totalOtherDeduction)}</b>,
    };

    const updatedData = [...data, footerRow];

    const fetchHeadAccounts = async () => {
        try {
            const accounts = await getHeadOfAccounts();
            if (accounts.data.status) {
                setHeadAccounts(accounts.data.result);
            }
        } catch (e) {
            // ignore
        }
    };

    useEffect(() => {
        fetchHeadAccounts();
    }, []);

    const handleAttachmentChange = (event) => {
        const files = event.target.files;
        setFormData((prev) => ({
            ...prev,
            attachments: files,
        }));
    };

    const handleChallanFormData = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const validateChallanForm = () => {
        const errors = [];

        if (!data.length) {
            errors.push('No invoices selected for challan.');
        }
        if (!String(formData.ref_doc_no || '').trim()) {
            errors.push('Reference Doc No is required.');
        }
        if (!formData.challan_date) {
            errors.push('Date is required.');
        }
        if (!formData.head_of_account_id) {
            errors.push('Head of Account is required.');
        }
        if (!formData.attachments?.length) {
            errors.push('Upload Document is required.');
        }
        if (!Number(totalOtherDeduction) || Number(totalOtherDeduction) <= 0) {
            errors.push('Other Deduction amount must be greater than zero.');
        }

        return errors;
    };

    const handleSubmit = async () => {
        const validationErrors = validateChallanForm();
        if (validationErrors.length) {
            toast.error(validationErrors[0], {
                position: toast.POSITION.TOP_CENTER,
            });
            return;
        }

        try {
            setLoading(true);
            const invoices = data.map((el) => el.id);
            const post_data = new FormData();

            for (let i = 0; i < formData.attachments.length; i += 1) {
                post_data.append('attachment[]', formData.attachments[i]);
            }

            post_data.append('invoices', invoices.join(','));
            post_data.append('ref_doc_no', String(formData.ref_doc_no).trim());
            post_data.append('challan_date', formData.challan_date);
            post_data.append('amount', totalOtherDeduction);
            post_data.append('head_of_account_id', formData.head_of_account_id);

            dispatch(ADD_CHALLAN_START());
            const response = await addOtherChallan(post_data);
            const addChallanResponseData = response.data;

            if (addChallanResponseData.status) {
                toast.success(addChallanResponseData.message || 'Challan added successfully', {
                    position: toast.POSITION.TOP_CENTER,
                });
                navigate('/other');
            } else {
                toast.error(addChallanResponseData.message, {
                    position: toast.POSITION.TOP_CENTER,
                });
                dispatch(ADD_CHALLAN_FAIL(addChallanResponseData.message));
            }
        } catch (error) {
            dispatch(ADD_CHALLAN_FAIL('error getting while updating challan'));
            toast.error(error?.response?.data?.message || 'Failed to add challan', {
                position: toast.POSITION.TOP_CENTER,
            });
        } finally {
            setLoading(false);
        }
    };

    const linksArray = [
        {
            href: '/other',
            label: 'Other',
        },
    ];

    return (
        <div className="p-3 add-other-challan-page">
            {loading && <Spinner />}

            <div className="row mb-3">
                <div className="col-md-6">
                    <h4 style={{ color: '#28326f', margin: 0 }}>Add Challan (Other)</h4>
                    <CustomBreadcrumbs linksArray={linksArray} title="Add Challan (Other)" />
                </div>
                <div className="col-md-6">
                    <div className="d-flex gap-2" style={{ width: 'auto', float: 'right' }}>
                        <button type="button" className="submit_button" onClick={handleSubmit} disabled={loading}>
                            Submit
                        </button>
                    </div>
                </div>
            </div>

            <div className="card p-3">
                <div className="add-other-challan-datatable-root">
                    <DataTable
                        customStyles={customStyles}
                        className="dataTables_wrapper"
                        columns={columns}
                        data={updatedData}
                        fixedHeader
                        fixedHeaderScrollHeight="320px"
                        conditionalRowStyles={[
                            {
                                when: (row) => row.isFooter,
                                style: {
                                    backgroundColor: '#f8f9fc',
                                    fontWeight: 'bold',
                                },
                            },
                        ]}
                    />
                </div>
            </div>

            <div className="card p-3 mt-3">
                <div className="row">
                    <div className="col-md-6 mb-3">
                        <FormControl fullWidth>
                            <TextField
                                id="ref_doc_no"
                                label="Reference Doc No"
                                variant="outlined"
                                type="text"
                                name="ref_doc_no"
                                value={formData.ref_doc_no || ''}
                                onChange={handleChallanFormData}
                                placeholder="Enter Ref Doc No"
                                required
                            />
                        </FormControl>
                    </div>

                    <div className="col-md-6 mb-3">
                        <FormControl fullWidth>
                            <TextField
                                id="challan_date"
                                label="Date"
                                variant="outlined"
                                type="date"
                                name="challan_date"
                                value={formData.challan_date || ''}
                                onChange={handleChallanFormData}
                                InputLabelProps={{ shrink: true }}
                                required
                            />
                        </FormControl>
                    </div>

                    <div className="col-md-6 mb-3">
                        <FormControl fullWidth>
                            <InputLabel id="head-of-account-label">Head of Account</InputLabel>
                            <Select
                                labelId="head-of-account-label"
                                id="head_of_account_id"
                                name="head_of_account_id"
                                value={formData.head_of_account_id || ''}
                                label="Head of Account"
                                onChange={handleChallanFormData}
                                required
                            >
                                <MenuItem value="" disabled>
                                    Select Account
                                </MenuItem>
                                {headAccounts.map((account) => (
                                    <MenuItem value={account.id} key={account.id}>
                                        {account.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>

                    <div className="col-md-6 mb-3">
                        <FormControl fullWidth>
                            <TextField
                                id="other_deduction_amount"
                                label="Other Deduction Amount"
                                variant="outlined"
                                type="text"
                                name="other_deduction_amount"
                                value={formatAmount(totalOtherDeduction)}
                                InputProps={{ readOnly: true }}
                                disabled
                                required
                            />
                        </FormControl>
                    </div>

                    <div className="col-md-6 mb-3">
                        <FormControl fullWidth>
                            <TextField
                                id="attachments"
                                label="Upload Document"
                                type="file"
                                name="attachments"
                                onChange={handleAttachmentChange}
                                inputProps={{
                                    accept: '.pdf,.jpeg,.jpg,.png',
                                    multiple: true,
                                }}
                                InputLabelProps={{ shrink: true }}
                                required
                            />
                        </FormControl>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddOtherChallan;
